/**
 * Static openid-configuration for the Azure AD B2C authority.
 *
 * MSAL keeps authority metadata in an in-memory store only, so without this
 * it fetches `{authority}/.well-known/openid-configuration` from
 * login.skypointcloud.com on every full page load — twice per cold login
 * (once before the redirect to B2C, once again on /callback). skyNote measured
 * that fetch at 0.5–1.1 s per load and eliminated it by passing the document
 * statically via `auth.authorityMetadata`. B2C endpoints are formulaic under
 * the policy authority; only the issuer needs the directory tenant id.
 *
 * Pure and MSAL-free so it can be unit-tested under node.
 */

export interface B2CAuthorityMetadata {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
  jwks_uri: string;
}

/**
 * Returns null when either input is missing or the authority is not a URL, so
 * the caller falls back to MSAL's network discovery instead of shipping a
 * half-built document.
 */
export function buildB2CAuthorityMetadata(
  authority: string | undefined,
  tenantId: string | undefined,
): B2CAuthorityMetadata | null {
  // The discovery document lives at `{authority}/.well-known/openid-configuration`
  // where the authority may carry a trailing `/v2.0`. The endpoints it lists hang
  // off the POLICY path (`…/policy/oauth2/v2.0/authorize`), not off `…/policy/v2.0`,
  // so strip the version segment before building them.
  const base = (authority ?? "")
    .trim()
    .replace(/\/+$/, "")
    .replace(/\/v2\.0$/i, "");
  const tid = (tenantId ?? "").trim();
  if (!base || !tid) return null;
  let host: string;
  try {
    host = new URL(base).host;
  } catch {
    return null;
  }
  if (!host) return null;
  return {
    issuer: `https://${host}/${tid}/v2.0/`,
    authorization_endpoint: `${base}/oauth2/v2.0/authorize`,
    token_endpoint: `${base}/oauth2/v2.0/token`,
    end_session_endpoint: `${base}/oauth2/v2.0/logout`,
    jwks_uri: `${base}/discovery/v2.0/keys`,
  };
}
